import { Plus, Camera, File, RotateCcw, SwitchCamera } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Collapse } from 'react-bootstrap'
import Modal from '../modal/Modal'
import Webcam from 'react-webcam'
import { base64ToFile } from '../../helpers/constants'
import toast from 'react-hot-toast'

function UploadFile(props) {
  const { handleFileChange } = props
  const fileInputRef = useRef(null)
  const imageCameraRef = useRef(null)
  const [showDropdown, setShowDropdown] = useState(false)
  const [showCamera, setShowCamera] = useState(false)
  const [imageCamera, setImageCamera] = useState(null)
  const [submitLoadingCamera, setSubmitLoadingCamera] = useState(false)
  const [facingMode, setFacingMode] = useState('user')

  useEffect(() => {
    const isMobile =
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      ) || window.innerWidth <= 768

    if (isMobile) {
      setFacingMode('environment')
    } else {
      setFacingMode('user')
    }
  }, [])

  const toggleCamera = () => {
    setFacingMode((prevMode) => (prevMode === 'user' ? 'environment' : 'user'))
  }

  const handleCaptureWebCam = useCallback(() => {
    const imageSrc = imageCameraRef.current?.getScreenshot()
    setImageCamera(imageSrc)
  }, [imageCameraRef, setImageCamera])

  const handleResetCapturWebCam = () => {
    imageCameraRef.current?.resetWebCam()
    setImageCamera(null)
  }

  const handleSubmitCamera = async () => {
    try {
      setSubmitLoadingCamera(true)
      const files = base64ToFile(
        imageCamera,
        `image-${new Date().getTime()}.jpg`
      )
      handleFileChange(files)
    } catch (err) {
      const errMsg = err?.response?.data?.errors?.message || 'An error occurred'
      toast.error(errMsg, {
        position: 'top-right',
        style: {
          fontSize: '14px',
          fontWeight: '600'
        }
      })
    } finally {
      setSubmitLoadingCamera(false)
      setShowCamera(false)
      setImageCamera(null)
      imageCameraRef.current?.resetWebCam()
    }
  }

  return (
    <>
      <div style={{ position: 'relative' }}>
        <div
          style={{
            border: '2px solid #dee2e6',
            borderRadius: '10px',
            width: '100px',
            height: '100px',
            padding: '30px',
            cursor: 'pointer'
          }}
          onClick={() => setShowDropdown(!showDropdown)}
          role="button"
        >
          <label htmlFor="file" style={{ cursor: 'pointer' }}>
            <Plus
              size={24}
              style={{ marginLeft: '10px' }}
              className="text-secondary"
            />
            <small style={{ marginLeft: '5px' }} className="text-secondary">
              Foto
            </small>
          </label>
          <input
            id="file-upload"
            type="file"
            onChange={handleFileChange}
            ref={fileInputRef}
            hidden={true}
            accept="image/*"
          />
        </div>
        {showDropdown && (
          <Collapse in={showDropdown}>
            <div
              style={{
                position: 'absolute',
                top: 60,
                left: 5,
                borderRadius: '5px',
                width: '150px',
                zIndex: 100,
                backgroundColor: 'white'
              }}
              className="shadow p-3"
            >
              <div className="d-flex flex-column gap-2">
                <Button
                  variant="transparent"
                  className="d-flex gap-2 border-0 p-0"
                  onClick={() => {
                    setShowCamera(true)
                    setShowDropdown(!showDropdown)
                  }}
                >
                  <div className="d-flex flex-column justify-content-center align-items-center">
                    <Camera size={14} color="black" />
                  </div>
                  <span className="fw-bold" style={{ fontSize: '13px' }}>
                    Camera Upload
                  </span>
                </Button>
                <hr style={{ marginTop: '-0.1rem' }} />
                <Button
                  variant="transparent"
                  className="d-flex gap-2 border-0 p-0"
                  style={{ marginTop: '-1rem' }}
                  onClick={(e) => {
                    e.stopPropagation()
                    fileInputRef.current?.click()
                  }}
                >
                  <div className="d-flex flex-column justify-content-center align-items-center">
                    <File size={14} color="black" />
                  </div>
                  <span
                    className="fw-bold"
                    style={{ fontSize: '13px', marginInlineStart: '0.3rem' }}
                  >
                    File Upload
                  </span>
                </Button>
              </div>
            </div>
          </Collapse>
        )}
      </div>
      <Modal
        show={showCamera}
        size="lg"
        handleClose={() => setShowCamera(false)}
      >
        <div className="d-flex flex-column gap-2">
          {!imageCamera && (
            <>
              <div style={{ height: '100%' }}>
                <Webcam
                  audio={false}
                  ref={imageCameraRef}
                  screenshotFormat="image/jpeg"
                  videoConstraints={{ facingMode }}
                  onUserMediaError={(err) => {
                    toast.error(
                      'Kamera tidak dapat diakses (Perlu koneksi HTTPS / Secure Context). Mengalihkan ke pemilih foto...',
                      { position: 'top-right' }
                    )
                    setShowCamera(false)
                    fileInputRef.current?.click()
                  }}
                  width={640}
                  height={480}
                  className="w-100 h-100"
                  style={{ borderRadius: '10px' }}
                />
              </div>
              <div className="w-100 d-flex justify-content-center align-items-center gap-3">
                <Button
                  variant="secondary"
                  className="rounded p-2"
                  style={{ width: '48px', height: '48px' }}
                  onClick={handleCaptureWebCam}
                  title="Ambil Foto"
                >
                  <Camera size={20} />
                </Button>
                <Button
                  variant="outline-secondary"
                  className="rounded p-2"
                  style={{ width: '48px', height: '48px' }}
                  onClick={toggleCamera}
                  title="Ganti Kamera"
                >
                  <SwitchCamera size={20} />
                </Button>
              </div>
            </>
          )}
          {imageCamera && (
            <>
              <img
                src={imageCamera}
                alt="Preview"
                style={{
                  border: '2px solid #dee2e6',
                  borderRadius: '10px',
                  width: '100%',
                  height: '100%',
                  marginBottom: '15px'
                }}
              />
              <div className="d-flex justify-content-between">
                <Button
                  variant="secondary"
                  onClick={handleResetCapturWebCam}
                  className="d-flex flex-row gap-2 justify-content-center"
                >
                  <div className="d-flex flex-column justify-content-center">
                    <RotateCcw size={14} color="white" />
                  </div>
                  <span>Retake</span>
                </Button>
                <Button
                  variant={`${submitLoadingCamera ? 'secondary' : 'success'}`}
                  onClick={handleSubmitCamera}
                  className={`fw-bold ${
                    submitLoadingCamera ? 'opacity-25' : ''
                  }`}
                  disabled={submitLoadingCamera}
                >
                  {submitLoadingCamera ? 'Uploading...' : 'Upload'}
                </Button>
              </div>
            </>
          )}
        </div>
      </Modal>
    </>
  )
}

export default UploadFile
